import styled from 'styled-components';

export const StepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
`;

export const StepTitle = styled.h1`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
  color: ${props => props.theme.colors.highBlack};
  text-align: center;
  margin: 0;
  padding: 0 16px;
`;

export const StepDescription = styled.p`
  font-size: 16px;
  font-weight: 400;
  color: ${props => props.theme.colors.black};
  text-align: center;
  line-height: 1.5;
  padding: 0 16px;
  margin: 0;
  box-sizing: border-box;
`;

export const TipsContainer = styled.div`
  width: calc(100% - 32px);
  margin: 0 16px;
  border: 1px solid ${props => props.theme.colors.secondary300};
  border-radius: 8px;
  padding: 16px 24px;
  box-sizing: border-box;
`;

export const TipsHeader = styled.h3`
  font-size: 16px;
  line-height: 1.5;
  font-weight: 600;
  color: ${props => props.theme.colors.black};
  margin: 0 0 24px 0;
`;

export const TipsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const TipItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-weight: 400;
  gap: 16px;
  font-size: 14px;
  line-height: 1.2;
  color: ${props => props.theme.colors.black};
`;

export const TipIcon = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: ${props => props.theme.colors.blackShades(0.05)};
  color: ${props => props.color || props.theme.colors.porterPrimary};
  border-radius: 4px;
`;

export const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: calc(100% - 32px);
  margin: 0 16px;
  box-sizing: border-box;
`;

export const PrimaryButton = styled.button`
  flex: 1;
  padding: 14px 24px;
  background-color: ${props => props.color || props.theme.colors.porterPrimary};
  color: ${props => props.theme.colors.white};
  border: none;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const SecondaryButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background-color: transparent;
  color: ${props => props.theme.colors.porterPrimary};
  border: 2px solid ${props => props.theme.colors.porterPrimary};
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.theme.colors.porterPrimary};
    color: ${props => props.theme.colors.white};
  }
`;

export const CaptureArea = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  aspect-ratio: 1;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: ${props => props.theme.colors.blackShades(0.1)};

  @media (max-height: 800px) {
    max-height: 360px;
  }

  @media (max-height: 650px) {
    max-height: 300px;
  }

  @media (max-height: 580px) {
    max-height: 250px;
  }
`;

export const CaptureOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    width: 60%;
    max-height: 85%;
    aspect-ratio: 3/4;
    border-radius: 50%;
    border: 4px dashed ${props => props.theme.colors.warning100};
    box-sizing: border-box;
    z-index: 10;
    pointer-events: none;

    @media (max-height: 650px) {
      width: 50%;
    }

    @media (max-height: 580px) {
      width: 45%;
    }
  }

  > * {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    pointer-events: auto;
  }
`;

export const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid ${props => props.theme.colors.blackShades(0.1)};
  border-top-color: ${props => props.theme.colors.porterPrimary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  color: ${props => props.theme.colors.secondary600};
  font-size: 14px;
`;

export const InfoIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${props => props.theme.colors.secondary600};
`;

export const InfoText = styled.div`
  flex: 1;
  font-size: 14px;
  color: ${props => props.theme.colors.secondary600};
  line-height: 1.2;
`;

export const TermsLink = styled.a`
  color: ${props => props.color || props.theme.colors.porterPrimary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const WrapperFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
`;

export const PreviewContainer = styled.div`
  position: relative;
  width: 100%;
  max-height: 360px;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: ${props => props.theme.colors.blackShades(0.1)};

  @media (max-height: 800px) {
    max-height: 360px;
  }

  @media (max-height: 650px) {
    max-height: 300px;
  }

  @media (max-height: 580px) {
    max-height: 250px;
  }
`;

export const ButtonsRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
`;

export const OutlinedButton = styled.button`
  flex: 1;
  padding: 14px 24px;
  background-color: transparent;
  color: ${props => props.color || props.theme.colors.porterSecondary};
  border: 1px solid
    ${props => props.color || props.theme.colors.porterSecondary};
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${props =>
      props.color || props.theme.colors.porterSecondary};
    color: ${props => props.theme.colors.white};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const SubmittingWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 0 16px;
  box-sizing: border-box;
  background-color: transparent;
`;

export const AvatarWrapper = styled.div`
  position: relative;
  width: 255px;
  height: 255px;
  border-radius: 50%;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.03);
  background-color: ${props => props.theme.colors.white};
  overflow: visible;
  z-index: 10;
  top: -20%;
`;

export const BinaryRainCanvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  mask-image: linear-gradient(
    to bottom,
    transparent 15%,
    black 40%,
    black 50%,
    transparent 80%
  );
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent 15%,
    black 40%,
    black 50%,
    transparent 80%
  );
`;

export const FaceCaptureImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

export const ShieldLockBadge = styled.div`
  position: absolute;
  bottom: -8%;
  right: 42%;
  width: 44px;
  height: 44px;
  background-color: ${props => props.theme.colors.green500};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.white};
  font-size: 26px;
  z-index: 20;
`;

export const LoadingBar = styled.div`
  position: relative;
  width: calc(100% - 32px);
  height: 10px;
  background-color: ${props => props.theme.colors.blackShades(0.05)};
  border-radius: 50px;
  margin: 24px 16px;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 40%;
    height: 10px;
    background-color: ${props =>
      props.color || props.theme.colors.porterPrimary};
    border-radius: 2px;
    animation: shimmer 1.5s ease-in-out infinite;

    @keyframes shimmer {
      0% {
        left: -100%;
      }
      50% {
        left: 100%;
      }
      100% {
        left: -100%;
      }
    }
  }
`;
